import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HandCoins, SlidersHorizontal } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, get, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { data, dinheiro, mesAno, nomePessoa, textoLivre } from '../../nucleo/formato';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { invalidarDespesas, type Despesa, type Reembolso } from './tipos';

/**
 * Reembolsos: as aprovadas de um funcionário viram uma conta a pagar no Contas ou, desde 15/10, um provento na folha; abaixo,
 * os reembolsos já gerados.
 */
export function Reembolsos() {
  const { t } = useTranslation('despesas');
  const s = useSessao();
  const [prazo, setPrazo] = useState(false);
  const colunas: Coluna<Reembolso>[] = [
    { chave: 'funcionario_nome', rotulo: t('campos.funcionario'), principal: true, render: (r) => <span className="font-medium">{nomePessoa(r.funcionario_nome) || '-'}</span> },
    { chave: 'despesas', rotulo: t('campos.despesas'), alinhar: 'direita', mono: true },
    { chave: 'valor_centavos', rotulo: t('campos.valor'), alinhar: 'direita', mono: true, render: (r) => dinheiro(r.valor_centavos) },
    { chave: 'vencimento', rotulo: t('campos.vencimento'), render: (r) => data(r.vencimento) },
    {
      chave: 'documento_numero',
      rotulo: t('campos.destino'),
      render: (r) => ondeFoi(r, t),
    },
  ];
  return (
    <>
      {podeNoWorkspace(s, 'despesas.configurar') && (
        <AcaoPrimaria>
          <Botao icone={<SlidersHorizontal className="size-4" />} onClick={() => setPrazo(true)}>
            {t('acoes.prazo')}
          </Botao>
        </AcaoPrimaria>
      )}
      {s.pode('despesas.aprovar') && <AReembolsar />}
      <Tabela<Reembolso> tela="despesas.reembolsos" caminho="/despesas/reembolsos" chaveConsulta={['despesas', 'reembolsos']} colunas={colunas} vazio={<Vazio texto={t('vazio.reembolsos')} />} />
      {prazo && <Prazo aoFechar={() => setPrazo(false)} />}
    </>
  );
}

/** Onde o reembolso foi parar: a folha do mês ou o número da conta a pagar (nulo sem `contas.ver`). */
function ondeFoi(r: Reembolso, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (r.destino === 'folha') return t('reembolso.naFolha', { mes: mesAno(r.folha_ano_mes) });
  return r.documento_numero ?? (r.documento_id ? t('semAcesso') : '-');
}

function AReembolsar() {
  const { t } = useTranslation('despesas');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [destino, setDestino] = useState(false);
  const q = useQuery({ queryKey: ['despesas', 'aprovadas'], queryFn: () => getTodos<Despesa>('/despesas/despesas', { estado: 'aprovada' }) });
  const itens = q.data?.itens ?? [];
  const escolhidas = itens.filter((d) => marcadas.has(d.id));
  const dono = escolhidas[0]?.funcionario_id;
  const concluir = async () => {
    setDestino(false);
    setMarcadas(new Set());
    await invalidarDespesas(qc);
    toast.sucesso(tn('comum.salvo'));
  };
  const total = escolhidas.reduce((a, d) => a + d.valor_centavos, 0);
  return (
    <Cartao className="mb-4" titulo={t('reembolso.aReembolsar')}>
      <p className="mb-3 text-sm text-tinta-3">{t('reembolso.ajuda')}</p>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : itens.length === 0 ? (
        <Vazio texto={t('vazio.aReembolsar')} />
      ) : (
        <>
          <ul className="flex flex-col divide-y divide-borda">
            {itens.map((d) => {
              const outro = !!dono && d.funcionario_id !== dono;
              return (
                <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <label className={`flex min-w-0 items-center gap-2 ${outro ? 'text-tinta-3' : 'cursor-pointer text-tinta-1'}`}>
                    <input
                      type="checkbox"
                      checked={marcadas.has(d.id)}
                      disabled={outro}
                      aria-label={t('reembolso.selecionar', { nome: d.descricao })}
                      onChange={(e) =>
                        setMarcadas((m) => {
                          const n = new Set(m);
                          if (e.target.checked) n.add(d.id);
                          else n.delete(d.id);
                          return n;
                        })
                      }
                    />
                    <span className="min-w-0">
                      <span className="block font-medium">{nomePessoa(d.funcionario_nome) || '-'}</span>
                      <span className="block text-tinta-3">
                        {data(d.data)} · {textoLivre(d.descricao, d.funcionario_nome)}
                      </span>
                    </span>
                  </label>
                  <span className="font-mono text-tinta-1">{dinheiro(d.valor_centavos)}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
            {escolhidas.length > 0 && <span className="text-sm text-tinta-2">{t('reembolso.marcadas', { count: escolhidas.length, valor: dinheiro(total) })}</span>}
            <Botao variante="primario" icone={<HandCoins className="size-4" />} aria-disabled={!escolhidas.length} onClick={() => escolhidas.length && setDestino(true)}>
              {t('acoes.reembolsar')}
            </Botao>
          </div>
        </>
      )}
      {destino && <Destino despesaIds={escolhidas.map((d) => d.id)} total={total} aoFechar={() => setDestino(false)} aoConcluir={concluir} />}
    </Cartao>
  );
}

interface CompetenciaQueRecebe {
  id: string;
  ano_mes: string;
  empresa_id: string | null;
  estado: string;
  pagamento: string;
}

/**
 * Destino do reembolso (respostas de 15/10): conta a pagar no Contas (padrão; sem parceiro ligado, o parceiro do CPF é criado)
 * ou provento numa competência da folha que ainda recebe lançamento (só para quem tem `folha.editar`).
 */
function Destino({ despesaIds, total, aoFechar, aoConcluir }: { despesaIds: string[]; total: number; aoFechar: () => void; aoConcluir: () => Promise<void> }) {
  const { t } = useTranslation('despesas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const podeFolha = s.pode('folha.editar');
  const comps = useQuery({
    queryKey: ['despesas', 'reembolso-competencias'],
    queryFn: () => get<{ itens: CompetenciaQueRecebe[] }>('/despesas/reembolsos/competencias'),
    enabled: podeFolha,
  });
  const gerar = useMutation({
    mutationFn: (v: Record<string, unknown>) => {
      const id = crypto.randomUUID();
      const folha = v.destino === 'folha';
      const corpo = folha ? { id, despesa_ids: despesaIds, destino: 'folha', competencia_id: v.competencia_id } : { id, despesa_ids: despesaIds, destino: 'contas', vencimento: v.vencimento || undefined };
      return post('/despesas/reembolsos', corpo, { 'Idempotency-Key': id });
    },
    onSuccess: aoConcluir,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const competencias = comps.data?.itens ?? [];
  const campos: DefCampo[] = [
    {
      nome: 'destino',
      rotulo: t('campos.destino'),
      tipo: 'selecao',
      obrigatorio: true,
      opcoes: [{ valor: 'contas', rotulo: t('reembolso.destinoContas') }, ...(podeFolha ? [{ valor: 'folha', rotulo: t('reembolso.destinoFolha') }] : [])],
      ajuda: t('reembolso.destinoAjuda'),
    },
    { nome: 'vencimento', rotulo: t('campos.vencimento'), tipo: 'data', ajuda: t('campos.vencimentoAjuda'), visivel: (v) => v.destino !== 'folha' },
    {
      nome: 'competencia_id',
      rotulo: t('campos.competencia'),
      tipo: 'selecao',
      obrigatorio: true,
      opcoes: competencias.map((c) => ({ valor: c.id, rotulo: t('reembolso.competencia', { mes: mesAno(c.ano_mes), pagamento: data(c.pagamento) }) })),
      ajuda: competencias.length ? t('reembolso.competenciaAjuda') : t('reembolso.semCompetencia'),
      visivel: (v) => v.destino === 'folha',
    },
  ];
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('acoes.reembolsar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-destino-reembolso" carregando={gerar.isPending}>
            {t('acoes.reembolsar')}
          </Botao>
        </>
      }
    >
      <p className="mb-3 text-sm text-tinta-2">{t('reembolso.marcadas', { count: despesaIds.length, valor: dinheiro(total) })}</p>
      {podeFolha && comps.isPending ? (
        <Carregando />
      ) : podeFolha && comps.isError ? (
        <Erro erro={comps.error} aoTentar={() => comps.refetch()} />
      ) : (
        <Formulario id="form-destino-reembolso" campos={campos} inicial={{ destino: 'contas', vencimento: '', competencia_id: '' }} aoEnviar={async (v) => void (await gerar.mutateAsync(v).catch(() => undefined))} />
      )}
    </Modal>
  );
}

function Prazo({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation('despesas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const q = useQuery({ queryKey: ['despesas', 'configuracao'], queryFn: () => get<{ prazo_reembolso_dias: number }>('/despesas/configuracao') });
  const campos: DefCampo[] = [{ nome: 'prazo_reembolso_dias', rotulo: t('campos.prazo'), tipo: 'numero', obrigatorio: true, min: 0, max: 60, passo: 1, ajuda: t('campos.prazoAjuda') }];
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('acoes.prazo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-prazo-despesas">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : (
        <Formulario
          id="form-prazo-despesas"
          campos={campos}
          inicial={{ prazo_reembolso_dias: q.data.prazo_reembolso_dias }}
          aoEnviar={async (v) => {
            await api('PUT', '/despesas/configuracao', { prazo_reembolso_dias: Number(v.prazo_reembolso_dias) });
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          }}
        />
      )}
    </Modal>
  );
}
